/**
 * Client Pipeline Membership Store
 *
 * Store Zustand per gestire le membership extra dei lead/clienti nelle pipeline.
 * La pipeline "primaria" resta su clients.pipeline_id — questa tabella gestisce solo le extra.
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import { ClientPipelineMembership } from '../types/leadPipeline';

interface ClientPipelineMembershipStore {
  // State
  membershipsByClient: Record<string, ClientPipelineMembership[]>; // clientId -> memberships[]
  loading: boolean;
  error: string | null;

  // Operations
  fetchMembershipsForClient: (clientId: string) => Promise<ClientPipelineMembership[]>;
  addToPipeline: (clientId: string, pipelineId: string, stageId: string) => Promise<ClientPipelineMembership | null>;
  removeFromPipeline: (clientId: string, pipelineId: string) => Promise<boolean>;
  updateStageInPipeline: (clientId: string, pipelineId: string, newStageId: string) => Promise<boolean>;
  fetchExtraByStage: (pipelineId: string, stageId: string) => Promise<string[]>; // returns client IDs
  getExtraPipelineCount: (clientId: string) => Promise<number>;
  countMembershipsForPipeline: (pipelineId: string) => Promise<number>;
  countMembershipsForStage: (stageId: string) => Promise<number>;

  // Bulk: conta le membership extra per un set di client IDs (per badge kanban)
  fetchExtraPipelineCounts: (clientIds: string[]) => Promise<Record<string, number>>;

  // Cleanup
  clearMembershipsForClient: (clientId: string) => void;
}

export const useClientPipelineMembershipStore = create<ClientPipelineMembershipStore>((set, get) => ({
  membershipsByClient: {},
  loading: false,
  error: null,

  fetchMembershipsForClient: async (clientId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_pipeline_memberships')
        .select(`
          *,
          pipeline:lead_pipelines!pipeline_id (id, name, color),
          pipeline_stage:lead_pipeline_stages!pipeline_stage_id (id, name, color, sort_order, is_won, is_lost)
        `)
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const memberships = (data || []) as ClientPipelineMembership[];
      set(state => ({
        membershipsByClient: {
          ...state.membershipsByClient,
          [clientId]: memberships
        }
      }));

      return memberships;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] fetchMembershipsForClient error:', err);
      set({ error: err.message });
      return [];
    }
  },

  addToPipeline: async (clientId: string, pipelineId: string, stageId: string) => {
    try {
      set({ loading: true, error: null });

      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('Organization ID not found');

      const { data, error } = await supabase
        .from('client_pipeline_memberships')
        .insert({
          client_id: clientId,
          pipeline_id: pipelineId,
          pipeline_stage_id: stageId,
          organization_id: organizationId,
          entered_at: new Date().toISOString(),
          last_stage_change_at: new Date().toISOString()
        })
        .select(`
          *,
          pipeline:lead_pipelines!pipeline_id (id, name, color),
          pipeline_stage:lead_pipeline_stages!pipeline_stage_id (id, name, color, sort_order, is_won, is_lost)
        `)
        .single();

      if (error) throw error;

      const membership = data as ClientPipelineMembership;

      // Aggiorna cache locale
      set(state => ({
        loading: false,
        membershipsByClient: {
          ...state.membershipsByClient,
          [clientId]: [...(state.membershipsByClient[clientId] || []), membership]
        }
      }));

      return membership;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] addToPipeline error:', err);
      set({ loading: false, error: err.message });
      return null;
    }
  },

  removeFromPipeline: async (clientId: string, pipelineId: string) => {
    try {
      set({ loading: true, error: null });

      const { error } = await supabase
        .from('client_pipeline_memberships')
        .delete()
        .eq('client_id', clientId)
        .eq('pipeline_id', pipelineId);

      if (error) throw error;

      // Aggiorna cache locale
      set(state => ({
        loading: false,
        membershipsByClient: {
          ...state.membershipsByClient,
          [clientId]: (state.membershipsByClient[clientId] || []).filter(m => m.pipeline_id !== pipelineId)
        }
      }));

      return true;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] removeFromPipeline error:', err);
      set({ loading: false, error: err.message });
      return false;
    }
  },

  updateStageInPipeline: async (clientId: string, pipelineId: string, newStageId: string) => {
    try {
      const { error } = await supabase
        .from('client_pipeline_memberships')
        .update({
          pipeline_stage_id: newStageId,
          last_stage_change_at: new Date().toISOString()
        })
        .eq('client_id', clientId)
        .eq('pipeline_id', pipelineId);

      if (error) throw error;

      // Aggiorna cache locale
      set(state => ({
        membershipsByClient: {
          ...state.membershipsByClient,
          [clientId]: (state.membershipsByClient[clientId] || []).map(m =>
            m.pipeline_id === pipelineId
              ? { ...m, pipeline_stage_id: newStageId, last_stage_change_at: new Date().toISOString() }
              : m
          )
        }
      }));

      return true;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] updateStageInPipeline error:', err);
      set({ error: err.message });
      return false;
    }
  },

  fetchExtraByStage: async (pipelineId: string, stageId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_pipeline_memberships')
        .select('client_id')
        .eq('pipeline_id', pipelineId)
        .eq('pipeline_stage_id', stageId);

      if (error) throw error;
      return (data || []).map(d => d.client_id);
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] fetchExtraByStage error:', err);
      return [];
    }
  },

  getExtraPipelineCount: async (clientId: string) => {
    try {
      const { count, error } = await supabase
        .from('client_pipeline_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId);

      if (error) throw error;
      return count || 0;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] getExtraPipelineCount error:', err);
      return 0;
    }
  },

  countMembershipsForPipeline: async (pipelineId: string) => {
    try {
      const { count, error } = await supabase
        .from('client_pipeline_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_id', pipelineId);

      if (error) throw error;
      return count || 0;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] countMembershipsForPipeline error:', err);
      return 0;
    }
  },

  countMembershipsForStage: async (stageId: string) => {
    try {
      const { count, error } = await supabase
        .from('client_pipeline_memberships')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_stage_id', stageId);

      if (error) throw error;
      return count || 0;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] countMembershipsForStage error:', err);
      return 0;
    }
  },

  fetchExtraPipelineCounts: async (clientIds: string[]) => {
    if (clientIds.length === 0) return {};

    try {
      const { data, error } = await supabase
        .from('client_pipeline_memberships')
        .select('client_id')
        .in('client_id', clientIds);

      if (error) throw error;

      // Conta per client_id
      const counts: Record<string, number> = {};
      (data || []).forEach(row => {
        counts[row.client_id] = (counts[row.client_id] || 0) + 1;
      });
      return counts;
    } catch (err: any) {
      console.error('[ClientPipelineMembershipStore] fetchExtraPipelineCounts error:', err);
      return {};
    }
  },

  clearMembershipsForClient: (clientId: string) => {
    set(state => {
      const updated = { ...state.membershipsByClient };
      delete updated[clientId];
      return { membershipsByClient: updated };
    });
  }
}));
